/**
 * Auto-generated API client for Innolek Platform
 * Source: Python FastAPI Backend OpenAPI spec
 * Generated at: 2026-01-02T11:06:27.063Z
 *
 * DO NOT EDIT THIS FILE MANUALLY
 * This file is auto-generated from Python backend OpenAPI spec
 */

import * as ApiTypes from './generated-types';

import { getFunctionsUrl } from '@/shared/config/environment';

type FetchOptions = {
    headers?: Record<string, string>;
    body?: unknown;
    queryParams?: Record<string, string>;
};

async function apiFetch<T>(path: string, method: string, options: FetchOptions = {}): Promise<T> {
    let url = `${getFunctionsUrl()}${path}`;

    // ✅ QUERY PARAMS: Add query parameters if provided
    if (options.queryParams && Object.keys(options.queryParams).length > 0) {
        const params = new URLSearchParams();
        Object.entries(options.queryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                params.append(key, value);
            }
        });
        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }
    }

    const fetchInit: RequestInit = {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
        credentials: 'include', // Include credentials for Python backend CORS
    };

    // ✅ SMART HANDLING: Detect FormData and handle appropriately
    if (options.body instanceof FormData) {
        // For FormData, don't set Content-Type (browser sets with boundary)
        delete fetchInit.headers!['Content-Type'];
        fetchInit.body = options.body;
    } else if (options.body) {
        // For regular JSON, stringify and set Content-Type
        fetchInit.body = JSON.stringify(options.body);
    }

    const response = await fetch(url, fetchInit);

    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`API call failed with status ${response.status}: ${errorBody}`);
    }
    return response.json();
}

export const api = {
    /**
     * Send Magic Link
     * @param queryParams.email - 
     */
    sendMagicLink: (queryParams: {
        /**  */
        email: string;
    }) => apiFetch<ApiTypes.MagicLinkResponse>(`/auth/magic-link`, 'POST', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Verify Token
     */
    verifyToken: () => apiFetch<ApiTypes.VerifyTokenResponse>(`/auth/verify`, 'GET'),

    /**
     * Get User Profile
     */
    getUserProfile: () => apiFetch<ApiTypes.GetUserProfileResponse>(`/users/profile`, 'GET'),

    /**
     * Update User Profile
     */
    updateUserProfile: (body: ApiTypes.UpdateUserProfileRequest) => apiFetch<ApiTypes.UpdateUserProfileResponse>(`/users/profile`, 'POST', { body }),

    /**
     * Get User Roles
     */
    getUserRoles: () => apiFetch<ApiTypes.GetUserRolesResponse>(`/users/roles`, 'GET'),

    /**
     * Check Admin Status
     */
    checkAdminStatus: () => apiFetch<ApiTypes.CheckAdminStatusResponse>(`/users/check-admin-status`, 'POST'),

    /**
     * Validate Discount Code
     */
    validateDiscount: (body: ApiTypes.ValidateDiscountRequest) => apiFetch<ApiTypes.ValidateDiscountResponse>(`/payments/discount/validate`, 'POST', { body }),

    /**
     * Process Payment
     */
    processPayment: (body: ApiTypes.PaymentProcessRequest) => apiFetch<ApiTypes.ProcessPaymentResponse>(`/payments/process`, 'POST', { body }),

    /**
     * Create Refund
     */
    createRefund: (pathParams: {
        order_id: string;
    }, body: ApiTypes.RefundRequest) => apiFetch<ApiTypes.RefundResponse>(`/payments/${pathParams.order_id}/refund`, 'POST', { body }),

    /**
     * List Refunds
     * @param queryParams.limit - 
     */
    listRefunds: (pathParams: {
        order_id: string;
    }, queryParams?: {
        /**  */
        limit?: number;
    }) => apiFetch<{ refund_id: string; status: string; amount: number; currency: string; reason?: string; created?: number }[]>(`/payments/${pathParams.order_id}/refunds`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get Refund
     */
    getRefund: (pathParams: {
        refund_id: string;
    }) => apiFetch<ApiTypes.RefundListItem>(`/payments/refunds/${pathParams.refund_id}`, 'GET'),

    /**
     * Get Payment Methods
     * @param queryParams.country_code - 
     */
    getPaymentMethods: (queryParams: {
        /**  */
        country_code: string;
    }) => apiFetch<ApiTypes.PaymentMethodsResponse>(`/payments/methods`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Create Checkout Session
     */
    createCheckout: (body: ApiTypes.CheckoutRequest) => apiFetch<ApiTypes.CheckoutResponse>(`/payments/checkout`, 'POST', { body }),

    /**
     * Get Payment Status
     */
    getPaymentStatus: (pathParams: {
        order_id: string;
    }) => apiFetch<ApiTypes.PaymentStatusResponse>(`/payments/status/${pathParams.order_id}`, 'GET'),

    /**
     * Calculate Pricing
     */
    calculatePricing: (body: ApiTypes.PricingRequest) => apiFetch<ApiTypes.CalculatePricingResponse>(`/pricing/calculate`, 'POST', { body }),

    /**
     * Get all figurine prices for a country
     * @param queryParams.country_code - ISO 2-letter country code (e.g., SE, US, DE)
     */
    getFigurinePrices: (queryParams: {
        /** ISO 2-letter country code (e.g., SE, US, DE) */
        country_code: string;
    }) => apiFetch<ApiTypes.FigurinePricesResponse>(`/pricing/figurines`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Get price for a Gelato product
     * @param queryParams.country_code - ISO 2-letter country code (REQUIRED - no auto-detection)
     * @param queryParams.quantity - 
     * @param queryParams.page_count - Page count for books
     */
    getProductPrice: (pathParams: {
        product_uid: string;
    }, queryParams: {
        /** ISO 2-letter country code (REQUIRED - no auto-detection) */
        country_code: string;
        /**  */
        quantity?: number;
        /** Page count for books */
        page_count?: number;
    }) => apiFetch<ApiTypes.ProductPriceResponse>(`/pricing/product/${pathParams.product_uid}`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Get current exchange rates
     */
    getExchangeRates: () => apiFetch<ApiTypes.ExchangeRatesResponse>(`/pricing/exchange-rates`, 'GET'),

    /**
     * Add Item To Cart
     */
    addItemToCartApiV1CartAddPost: (body: ApiTypes.AddLineItemRequest) => apiFetch<ApiTypes.CartResponse>(`/cart/add`, 'POST', { body }),

    /**
     * Get Cart
     */
    getCartApiV1CartSessionIdGet: (pathParams: {
        session_id: string;
    }) => apiFetch<ApiTypes.CartResponse>(`/cart/${pathParams.session_id}`, 'GET'),

    /**
     * Clear Cart
     */
    clearCartApiV1CartSessionIdDelete: (pathParams: {
        session_id: string;
    }) => apiFetch<ApiTypes.CartResponse>(`/cart/${pathParams.session_id}`, 'DELETE'),

    /**
     * Get Cart Total
     * @param queryParams.shipping_method_id - 
     */
    getCartTotalApiV1CartSessionIdTotalGet: (pathParams: {
        session_id: string;
    }, queryParams?: {
        /**  */
        shipping_method_id?: string;
    }) => apiFetch<ApiTypes.CartTotalResponse>(`/cart/${pathParams.session_id}/total`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Update Cart Item
     */
    updateCartItemApiV1CartSessionIdItemsItemIdPatch: (pathParams: {
        session_id: string;
        item_id: string;
    }, body: ApiTypes.UpdateQuantityRequest) => apiFetch<ApiTypes.CartResponse>(`/cart/${pathParams.session_id}/items/${pathParams.item_id}`, 'PATCH', { body }),

    /**
     * Remove Cart Item
     */
    removeCartItemApiV1CartSessionIdItemsItemIdDelete: (pathParams: {
        session_id: string;
        item_id: string;
    }) => apiFetch<ApiTypes.CartResponse>(`/cart/${pathParams.session_id}/items/${pathParams.item_id}`, 'DELETE'),

    /**
     * Update Cart Country
     */
    updateCartCountry: (pathParams: {
        session_id: string;
    }, body: ApiTypes.UpdateCartCountryRequest) => apiFetch<ApiTypes.CartResponse>(`/cart/${pathParams.session_id}/country`, 'POST', { body }),

    /**
     * Remove Cart Item From Cookie
     */
    removeCartItemFromCookieApiV1CartItemsItemIdDelete: (pathParams: {
        item_id: string;
    }) => apiFetch<ApiTypes.CartResponse>(`/cart/items/${pathParams.item_id}`, 'DELETE'),

    /**
     * Calculate Shipping
     */
    calculateShipping: (body: ApiTypes.CalculateShippingRequest) => apiFetch<ApiTypes.CalculateShippingResponse>(`/shipping/calculate`, 'POST', { body }),

    /**
     * Get Shipping Options
     * @param queryParams.order_id - 
     * @param queryParams.country_code - 
     */
    getShippingOptions: (queryParams?: {
        /**  */
        order_id?: string;
        /**  */
        country_code?: string;
    }) => apiFetch<ApiTypes.ShippingOptionsResponse>(`/shipping/options`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get Admin Statistics
     */
    getAdminStatistics: () => apiFetch<ApiTypes.AdminStatsResponse>(`/admin/statistics`, 'GET'),

    /**
     * Get Admin Stats
     */
    getAdminStats: () => apiFetch<ApiTypes.AdminStatsResponse>(`/admin/stats`, 'GET'),

    /**
     * Get All Orders Admin
     * @param queryParams.skip - Pagination offset
     * @param queryParams.limit - Results per page
     * @param queryParams.status - Filter by order status
     * @param queryParams.payment_status - Filter by payment status
     * @param queryParams.search - Search by order_number or email
     * @param queryParams.pending_gelato - Filter orders paid but not sent to Gelato
     */
    getAllOrdersAdmin: (queryParams?: {
        /** Pagination offset */
        skip?: number;
        /** Results per page */
        limit?: number;
        /** Filter by order status */
        status?: string;
        /** Filter by payment status */
        payment_status?: string;
        /** Search by order_number or email */
        search?: string;
        /** Filter orders paid but not sent to Gelato */
        pending_gelato?: boolean;
    }) => apiFetch<ApiTypes.GetAllOrdersResponse>(`/admin/orders`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get Performance Dashboard
     */
    getPerformanceDashboard: () => apiFetch<ApiTypes.PerformanceDashboardResponse>(`/admin/performance`, 'GET'),

    /**
     * Get Performance Dashboard Legacy
     */
    getPerformanceDashboardLegacy: () => apiFetch<ApiTypes.PerformanceDashboardResponse>(`/admin/performance-dashboard`, 'GET'),

    /**
     * Check Test Mode Eligibility
     */
    checkTestModeEligibility: (body: ApiTypes.TestModeEligibilityRequest) => apiFetch<ApiTypes.TestModeEligibilityResponse>(`/admin/check-test-mode-eligibility`, 'POST', { body }),

    /**
     * Debug Headers
     */
    debugHeaders: () => apiFetch<ApiTypes.DebugHeadersResponse>(`/admin/debug-headers`, 'GET'),

    /**
     * Get Revenue Analytics
     */
    getRevenueAnalytics: () => apiFetch<ApiTypes.RevenueAnalyticsResponse>(`/admin/analytics/revenue`, 'GET'),

    /**
     * Get Conversion Analytics
     */
    getConversionAnalytics: () => apiFetch<ApiTypes.ConversionAnalyticsResponse>(`/admin/analytics/conversion`, 'GET'),

    /**
     * Get Orders By Method
     */
    getOrdersByMethod: () => apiFetch<ApiTypes.OrdersByMethodResponse>(`/admin/analytics/orders-by-method`, 'GET'),

    /**
     * Get Failed Payments
     * @param queryParams.limit - Maximum records to return
     */
    getFailedPayments: (queryParams?: {
        /** Maximum records to return */
        limit?: number;
    }) => apiFetch<ApiTypes.FailedPaymentsResponse>(`/admin/analytics/failed-payments`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get Order Queue
     * @param queryParams.limit - Results per page
     * @param queryParams.offset - Pagination offset
     */
    getAdminOrderQueue: (queryParams?: {
        /** Results per page */
        limit?: number;
        /** Pagination offset */
        offset?: number;
    }) => apiFetch<ApiTypes.OrderQueueResponse>(`/admin/orders/queue`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get Queue Stats
     */
    getAdminQueueStats: () => apiFetch<ApiTypes.QueueStats>(`/admin/orders/queue/stats`, 'GET'),

    /**
     * Get Order Detail
     */
    getAdminOrderDetail: (pathParams: {
        order_id: string;
    }) => apiFetch<ApiTypes.OrderDetail>(`/admin/orders/${pathParams.order_id}`, 'GET'),

    /**
     * Update Order Status
     * @param queryParams.status - New order status
     * @param queryParams.payment_status - New payment status
     */
    updateOrderStatus: (pathParams: {
        order_id: string;
    }, queryParams?: {
        /** New order status */
        status?: string;
        /** New payment status */
        payment_status?: string;
    }) => apiFetch<ApiTypes.OrderDetail>(`/admin/orders/${pathParams.order_id}/update-status`, 'POST', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Approve Order
     */
    approveOrder: (pathParams: {
        order_id: string;
    }, body: ApiTypes.ApproveOrderRequest) => apiFetch<ApiTypes.ApprovalResult>(`/admin/orders/${pathParams.order_id}/approve`, 'POST', { body }),

    /**
     * Reject Order
     */
    rejectOrder: (pathParams: {
        order_id: string;
    }, body: ApiTypes.RejectOrderRequest) => apiFetch<ApiTypes.RejectionResult>(`/admin/orders/${pathParams.order_id}/reject`, 'POST', { body }),

    /**
     * Bulk Approve Orders
     */
    bulkApproveOrders: (body: ApiTypes.BulkApproveRequest) => apiFetch<ApiTypes.BulkApprovalResult>(`/admin/orders/bulk-approve`, 'POST', { body }),

    /**
     * Get Supplier Status
     */
    getOrderSupplierStatus: (pathParams: {
        order_id: string;
    }) => apiFetch<ApiTypes.SupplierStatus>(`/admin/orders/${pathParams.order_id}/supplier-status`, 'GET'),

    /**
     * Get Colors Grouped
     */
    getBusinessLogicColorsGrouped: () => apiFetch<ApiTypes.ColorsGroupedResponse>(`/business-logic/colors/grouped`, 'GET'),

    /**
     * Get Personalities
     */
    getPersonalities: () => apiFetch<ApiTypes.PersonalitiesResponse>(`/business-logic/personalities`, 'GET'),

    /**
     * Get Environments
     */
    getEnvironments: () => apiFetch<ApiTypes.EnvironmentsResponse>(`/business-logic/environments`, 'GET'),

    /**
     * Get Styles
     */
    getStyles: () => apiFetch<ApiTypes.StylesResponse>(`/business-logic/styles`, 'GET'),

    /**
     * Get Occasions
     */
    getOccasions: () => apiFetch<ApiTypes.OccasionsResponse>(`/business-logic/occasions`, 'GET'),

    /**
     * Get Special Features
     */
    getSpecialFeatures: () => apiFetch<ApiTypes.SpecialFeaturesResponse>(`/business-logic/special-features`, 'GET'),

    /**
     * Get Product Sizes
     */
    getProductSizes: () => apiFetch<ApiTypes.ProductSizesResponse>(`/business-logic/product-sizes`, 'GET'),

    /**
     * Get Add Ons
     */
    getAddOns: () => apiFetch<ApiTypes.AddOnsResponse>(`/business-logic/add-ons`, 'GET'),

    /**
     * Get Categories
     */
    getCategories: () => apiFetch<ApiTypes.CategoriesResponse>(`/business-logic/categories`, 'GET'),

    /**
     * Get All Business Logic
     * @param queryParams.customer_country_code - Customer country code for currency conversion
     */
    getBusinessLogic: (queryParams: {
        /** Customer country code for currency conversion */
        customer_country_code: string;
    }) => apiFetch<ApiTypes.AllBusinessLogicResponse>(`/business-logic/all`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Batch Update Order
     */
    batchUpdateOrder: (body: ApiTypes.BatchUpdateRequest) => apiFetch<ApiTypes.BatchUpdateResponse>(`/batch-update/order`, 'POST', { body }),

    /**
     * Get Supported Countries
     */
    getSupportedCountries: () => apiFetch<ApiTypes.SupportedCountriesResponse>(`/countries/supported`, 'GET'),

    /**
     * Debug Geolocation
     */
    debugGeolocation: () => apiFetch<ApiTypes.DebugGeolocationResponse>(`/countries/debug-geo`, 'GET'),

    /**
     * Stripe Webhook
     */
    stripeWebhook: () => apiFetch<ApiTypes.StripeWebhookResponse>(`/webhooks/stripe`, 'POST'),

    /**
     * Gelato Webhook
     */
    gelatoWebhook: () => apiFetch<ApiTypes.GelatoWebhookResponse>(`/webhooks/gelato`, 'POST'),

    /**
     * Tripo3D Webhook
     */
    tripo3dWebhook: () => apiFetch<ApiTypes.Tripo3DWebhookResponse>(`/webhooks/tripo3d`, 'POST'),

    /**
     * Csm Webhook
     */
    csmWebhook: () => apiFetch<ApiTypes.CSMWebhookResponse>(`/webhooks/csm`, 'POST'),

    /**
     * Get Toy Metadata
     * @param queryParams.prompt_id - Prompt ID
     */
    getToyMetadata: (queryParams?: {
        /** Prompt ID */
        prompt_id?: string;
    }) => apiFetch<ApiTypes.ToyMetadataResponse>(`/metadata/toy`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Detect Geo Location
     */
    detectGeo: () => apiFetch<ApiTypes.GeoDetectionResponse>(`/geo`, 'GET'),

    /**
     * Get Product Definitions
     */
    getProductDefinitions: () => apiFetch<ApiTypes.ProductDefinitionsResponse>(`/products/definitions`, 'GET'),

    /**
     * Get Gelato Book Quote
     * @param queryParams.productDefinitionId - Product definition ID
     * @param queryParams.customer_country_code - Customer country code
     * @param queryParams.pageCount - Number of pages
     * @param queryParams.quantity - Quantity
     */
    getGelatoBookQuote: (queryParams: {
        /** Product definition ID */
        productDefinitionId: string;
        /** Customer country code */
        customer_country_code?: string;
        /** Number of pages */
        pageCount?: number;
        /** Quantity */
        quantity?: number;
    }) => apiFetch<ApiTypes.GetGelatoBookQuoteResponse>(`/gelato/book-quote`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Get Gelato Product Info
     * @param queryParams.productDefinitionId - Product definition ID
     * @param queryParams.countryCode - Country code
     */
    getGelatoProductInfo: (queryParams: {
        /** Product definition ID */
        productDefinitionId: string;
        /** Country code */
        countryCode?: string;
    }) => apiFetch<ApiTypes.GetGelatoProductInfoResponse>(`/gelato/product-info`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Sync Gelato Products
     */
    syncGelatoProducts: (body: ApiTypes.Body_syncGelatoProducts) => apiFetch<ApiTypes.SyncGelatoProductsResponse>(`/gelato/sync-products`, 'POST', { body }),

    /**
     * Get figurine options
     * @param queryParams.country_code - ISO country code for pricing
     */
    getCatalogFigurines: (queryParams: {
        /** ISO country code for pricing */
        country_code: string;
    }) => apiFetch<ApiTypes.FigurineListResponse>(`/catalog/figurines`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Get figurine detail
     * @param queryParams.country_code - ISO country code for pricing
     */
    getCatalogFigurineDetail: (pathParams: {
        size_code: string;
    }, queryParams: {
        /** ISO country code for pricing */
        country_code: string;
    }) => apiFetch<ApiTypes.FigurineDetailResponse>(`/catalog/figurines/${pathParams.size_code}`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Get figurine quality tiers
     * @param queryParams.country_code - ISO country code for pricing
     * @param queryParams.size_code - Size code for base price calculation
     */
    getCatalogFigurineQualities: (queryParams?: {
        /** ISO country code for pricing */
        country_code?: string;
        /** Size code for base price calculation */
        size_code?: string;
    }) => apiFetch<ApiTypes.QualityTierListResponse>(`/catalog/figurine-qualities`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get product categories
     */
    getCatalogCategories: () => apiFetch<ApiTypes.CategoryListResponse>(`/catalog/categories`, 'GET'),

    /**
     * Get products
     * @param queryParams.country_code - ISO country code for pricing
     * @param queryParams.category - Filter by category slug
     * @param queryParams.limit - Max products to return
     * @param queryParams.offset - Pagination offset
     */
    getCatalogProducts: (queryParams: {
        /** ISO country code for pricing */
        country_code: string;
        /** Filter by category slug */
        category?: string;
        /** Max products to return */
        limit?: number;
        /** Pagination offset */
        offset?: number;
    }) => apiFetch<ApiTypes.ProductListResponse>(`/catalog/products`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Get product detail
     * @param queryParams.country_code - ISO country code for pricing
     */
    getCatalogProductDetail: (pathParams: {
        product_uid: string;
    }, queryParams: {
        /** ISO country code for pricing */
        country_code: string;
    }) => apiFetch<ApiTypes.ProductDetailResponse>(`/catalog/products/${pathParams.product_uid}`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Get price quote for a product
     */
    getCatalogProductPriceQuote: (pathParams: {
        product_uid: string;
    }, body: ApiTypes.ProductPriceQuoteRequest) => apiFetch<ApiTypes.ProductPriceQuoteResponse>(`/catalog/products/${pathParams.product_uid}/price`, 'POST', { body }),

    /**
     * Get product mockup data
     */
    getCatalogProductMockup: (pathParams: {
        product_uid: string;
    }) => apiFetch<ApiTypes.MockupDataResponse>(`/catalog/products/${pathParams.product_uid}/mockup`, 'GET'),

    /**
     * Get figurine mockup data
     */
    getCatalogFigurineMockup: (pathParams: {
        size_code: string;
    }) => apiFetch<ApiTypes.MockupDataResponse>(`/catalog/figurines/${pathParams.size_code}/mockup`, 'GET'),

    /**
     * Create Chat Session
     */
    createChatSessionApiV1ChatSessionsPost: (body: ApiTypes.CreateChatSessionRequest) => apiFetch<ApiTypes.ChatSessionResponse>(`/chat/sessions`, 'POST', { body }),

    /**
     * Chat Message Standardized
     */
    chatMessageStandardizedApiV1ChatSessionsSessionIdMessagePost: (pathParams: {
        session_id: string;
    }) => apiFetch<Record<string, unknown>>(`/chat/sessions/${pathParams.session_id}/message`, 'POST'),

    /**
     * Chat Message
     */
    chatMessageApiV1ChatMessagePost: (body: ApiTypes.ChatRequest) => apiFetch<Record<string, unknown>>(`/chat/message`, 'POST', { body }),

    /**
     * Get orders by email or session
     */
    getOrdersBySessionApiV1OrdersBySessionPost: (body: ApiTypes.OrdersBySessionRequest) => apiFetch<ApiTypes.OrdersBySessionResponse>(`/orders/by-session`, 'POST', { body }),

    /**
     * Update delivery information
     */
    updateDeliveryApiV1OrdersOrderIdDeliveryPut: (pathParams: {
        order_id: string;
    }, body: ApiTypes.UpdateDeliveryRequest) => apiFetch<ApiTypes.UpdateDeliveryResponse>(`/orders/${pathParams.order_id}/delivery`, 'PUT', { body }),

    /**
     * Get order by ID
     */
    getOrderApiV1OrdersOrderIdGet: (pathParams: {
        order_id: string;
    }) => apiFetch<ApiTypes.OrderResponse>(`/orders/${pathParams.order_id}`, 'GET'),

    /**
     * Lookup Order
     */
    lookupOrderApiV1OrderLookup_Post: (body: ApiTypes.OrderLookupRequest) => apiFetch<ApiTypes.OrderLookupResponse>(`/order-lookup/`, 'POST', { body }),

    /**
     * Get Order Total
     * @param queryParams.order_id - Order ID
     */
    getOrderTotalApiV1OrderTotal_Get: (queryParams: {
        /** Order ID */
        order_id: string;
    }) => apiFetch<ApiTypes.OrderTotalResponse>(`/order-total/`, 'GET', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Order Confirmation
     */
    orderConfirmation: (body: ApiTypes.OrderConfirmationRequest) => apiFetch<ApiTypes.OrderConfirmationResponse>(`/order-confirmation/`, 'POST', { body }),

    /**
     * Set Order Paid
     */
    setOrderPaid: (body: ApiTypes.Body_setOrderPaid) => apiFetch<ApiTypes.SetOrderPaidResponse>(`/order-confirmation/set-paid`, 'POST', { body }),

    /**
     * Get order tracking information
     */
    getTrackingInfoApiV1TrackingOrderIdGet: (pathParams: {
        order_id: string;
    }) => apiFetch<ApiTypes.OrderTrackingResponse>(`/tracking/${pathParams.order_id}`, 'GET'),

    /**
     * Get order status timeline
     */
    getStatusTimelineApiV1TrackingOrderIdTimelineGet: (pathParams: {
        order_id: string;
    }) => apiFetch<ApiTypes.TimelineResponse>(`/tracking/${pathParams.order_id}/timeline`, 'GET'),

    /**
     * Update order status
     */
    updateOrderStatusApiV1TrackingOrderIdStatusPost: (pathParams: {
        order_id: string;
    }, body: ApiTypes.UpdateStatusRequest) => apiFetch<ApiTypes.UpdateStatusResponse>(`/tracking/${pathParams.order_id}/status`, 'POST', { body }),

    /**
     * Get tracking for orders by email
     * @param queryParams.limit - 
     */
    getTrackingByEmailApiV1TrackingByEmailEmailGet: (pathParams: {
        email: string;
    }, queryParams?: {
        /**  */
        limit?: number;
    }) => apiFetch<{ order_id: string; order_number: string; current_status: string; current_status_display: { title: string; description: string; icon: string }; payment_status: string; timeline: { id: string; status: string; title: string; description: string; icon: string; notes?: string; metadata?: Record<string, unknown>; changed_by: string; created_at?: string }[]; tracking: { tracking_number?: string; tracking_url?: string; carrier?: string }; estimated_delivery?: { estimated_date: string; estimated_range: string; confidence: string }; product_groups?: { fulfillment_provider: string; provider_display_name: string; products: { id: string; name: string; quantity: number; image_url?: string; catalog_category?: string }[]; estimated_min_delivery_days?: number; estimated_max_delivery_days?: number; estimated_delivery_date?: string; estimated_range?: string; delivery_estimate_source?: string; last_updated?: string; status_message?: string }[]; delivery_address?: { full_name: string; street_address: string; city: string; postal_code: string; country: string; phone?: string }; created_at?: string; updated_at?: string }[]>(`/tracking/by-email/${pathParams.email}`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get tracking by order number
     */
    getTrackingByOrderNumberApiV1TrackingByOrderNumberOrderNumberGet: (pathParams: {
        order_number: string;
    }) => apiFetch<ApiTypes.OrderTrackingResponse>(`/tracking/by-order-number/${pathParams.order_number}`, 'GET'),

    /**
     * Refresh delivery estimates
     */
    refreshDeliveryEstimatesApiV1TrackingOrderIdRefreshEstimatesPost: (pathParams: {
        order_id: string;
    }) => apiFetch<ApiTypes.RefreshDeliveryEstimatesResponse>(`/tracking/${pathParams.order_id}/refresh-estimates`, 'POST'),

    /**
     * Upload photo
     */
    uploadPhotoApiV1PhotosPost: (body: ApiTypes.Body_upload_photo_api_v1_photos_post) => apiFetch<ApiTypes.PhotoAssetResponse>(`/photos`, 'POST', { body }),

    /**
     * Create from URL
     * @param queryParams.url - 
     * @param queryParams.source_type - 
     * @param queryParams.source_id - 
     */
    createFromUrlApiV1PhotosFromUrlPost: (queryParams: {
        /**  */
        url: string;
        /**  */
        source_type: 'order_upload' | 'user_profile' | 'design_input' | 'reference';
        /**  */
        source_id?: string;
    }) => apiFetch<ApiTypes.PhotoAssetResponse>(`/photos/from-url`, 'POST', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Get photo
     */
    getPhotoApiV1PhotosPhotoIdGet: (pathParams: {
        photo_id: string;
    }) => apiFetch<ApiTypes.PhotoAssetResponse>(`/photos/${pathParams.photo_id}`, 'GET'),

    /**
     * Delete photo
     */
    deletePhotoApiV1PhotosPhotoIdDelete: (pathParams: {
        photo_id: string;
    }) => apiFetch<void>(`/photos/${pathParams.photo_id}`, 'DELETE'),

    /**
     * Get photos by source
     */
    getPhotosBySourceApiV1PhotosBySourceSourceTypeSourceIdGet: (pathParams: {
        source_type: string;
        source_id: string;
    }) => apiFetch<{ id: string; original_url: string; processed_url?: string; thumbnail_url?: string; source_type: 'order_upload' | 'user_profile' | 'design_input' | 'reference'; source_id?: string; mime_type?: string; file_size_bytes?: number; width?: number; height?: number; ai_analysis?: Record<string, unknown>; ai_analysis_provider?: string; ai_analysis_model?: string; ai_analyzed_at?: string; storage_provider?: string; is_deleted?: boolean; created_at: string; updated_at?: string }[]>(`/photos/by-source/${pathParams.source_type}/${pathParams.source_id}`, 'GET'),

    /**
     * Analyze photo
     * @param queryParams.provider - 
     */
    analyzePhotoApiV1PhotosPhotoIdAnalyzePost: (pathParams: {
        photo_id: string;
    }, queryParams?: {
        /**  */
        provider?: string;
    }) => apiFetch<ApiTypes.PhotoAssetResponse>(`/photos/${pathParams.photo_id}/analyze`, 'POST', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Create thumbnail
     * @param queryParams.max_size - 
     */
    createThumbnailApiV1PhotosPhotoIdThumbnailPost: (pathParams: {
        photo_id: string;
    }, queryParams?: {
        /**  */
        max_size?: number;
    }) => apiFetch<ApiTypes.PhotoAssetResponse>(`/photos/${pathParams.photo_id}/thumbnail`, 'POST', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Update processed URL
     * @param queryParams.processed_url - 
     */
    updateProcessedUrlApiV1PhotosPhotoIdProcessedUrlPatch: (pathParams: {
        photo_id: string;
    }, queryParams: {
        /**  */
        processed_url: string;
    }) => apiFetch<ApiTypes.PhotoAssetResponse>(`/photos/${pathParams.photo_id}/processed-url`, 'PATCH', { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) }),

    /**
     * Create generation session
     */
    createSessionApiV1GenerationSessionsPost: (body: ApiTypes.CreateGenerationSessionRequest) => apiFetch<ApiTypes.GenerationSessionResponse>(`/generation/sessions`, 'POST', { body }),

    /**
     * List user sessions
     * @param queryParams.limit - 
     * @param queryParams.offset - 
     */
    listSessionsApiV1GenerationSessionsGet: (queryParams?: {
        /**  */
        limit?: number;
        /**  */
        offset?: number;
    }) => apiFetch<ApiTypes.GenerationSessionListResponse>(`/generation/sessions`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get generation session
     */
    getSessionApiV1GenerationSessionsSessionIdGet: (pathParams: {
        session_id: string;
    }) => apiFetch<ApiTypes.GenerationSessionResponse>(`/generation/sessions/${pathParams.session_id}`, 'GET'),

    /**
     * Build prompt
     */
    buildPromptApiV1GenerationSessionsSessionIdBuildPromptPost: (pathParams: {
        session_id: string;
    }) => apiFetch<ApiTypes.GenerationSessionResponse>(`/generation/sessions/${pathParams.session_id}/build-prompt`, 'POST'),

    /**
     * Generate variants
     */
    generateVariantsApiV1GenerationSessionsSessionIdGeneratePost: (pathParams: {
        session_id: string;
    }, body: ApiTypes.GenerateVariantsRequest) => apiFetch<{ id: string; session_id: string; variant_index: number; variant_type?: string; style_name?: string; theme_name?: string; image_url?: string; thumbnail_url?: string; width?: number; height?: number; provider?: string; generation_settings?: Record<string, unknown>; seed?: number; generation_time_ms?: number; is_selected?: boolean; selected_at?: string; user_rating?: number; user_feedback?: string; converted_to_design_asset_id?: string; converted_at?: string; created_at: string }[]>(`/generation/sessions/${pathParams.session_id}/generate`, 'POST', { body }),

    /**
     * Select variant
     */
    selectVariantApiV1GenerationVariantsVariantIdSelectPost: (pathParams: {
        variant_id: string;
    }, body: ApiTypes.SelectVariantRequest) => apiFetch<ApiTypes.GenerationVariantResponse>(`/generation/variants/${pathParams.variant_id}/select`, 'POST', { body }),

    /**
     * Select style and generate
     */
    selectStyleAndGenerateApiV1GenerationSessionsSessionIdSelectStylePost: (pathParams: {
        session_id: string;
    }, body: ApiTypes.SelectStyleRequest) => apiFetch<ApiTypes.GenerationSessionSummaryResponse>(`/generation/sessions/${pathParams.session_id}/select-style`, 'POST', { body }),

    /**
     * Convert to design
     */
    convertToDesignApiV1GenerationVariantsVariantIdConvertPost: (pathParams: {
        variant_id: string;
    }, body: ApiTypes.ConvertToDesignRequest) => apiFetch<ApiTypes.DesignAssetResponse>(`/generation/variants/${pathParams.variant_id}/convert`, 'POST', { body }),

    /**
     * Quick generate
     * @param queryParams.num_variants - 
     */
    quickGenerateApiV1GenerationQuickGeneratePost: (body: ApiTypes.CreateGenerationSessionRequest) => apiFetch<ApiTypes.GenerationSessionResponse>(`/generation/quick-generate`, 'POST', { body }),

    /**
     * Quick generate from photo
     */
    quickGeneratePhotoApiV1GenerationQuickGeneratePhotoPost: (body: ApiTypes.Body_quick_generate_photo_api_v1_generation_quick_generate_photo_post) => apiFetch<ApiTypes.GenerationSessionResponse>(`/generation/quick-generate-photo`, 'POST', { body }),

    /**
     * Search designs
     * @param queryParams.query - Search text
     * @param queryParams.category_id - Filter by category
     * @param queryParams.owner_id - Filter by owner
     * @param queryParams.public_only - Only show public designs
     * @param queryParams.featured_only - Only show featured designs
     * @param queryParams.limit - Max results
     * @param queryParams.offset - Offset for pagination
     */
    searchDesignsApiV1DesignsGet: (queryParams?: {
        /** Search text */
        query?: string;
        /** Filter by category */
        category_id?: string;
        /** Filter by owner */
        owner_id?: string;
        /** Only show public designs */
        public_only?: boolean;
        /** Only show featured designs */
        featured_only?: boolean;
        /** Max results */
        limit?: number;
        /** Offset for pagination */
        offset?: number;
    }) => apiFetch<ApiTypes.DesignAssetListResponse>(`/designs`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get featured designs
     * @param queryParams.limit - Max results
     */
    getFeaturedDesignsApiV1DesignsFeaturedGet: (queryParams?: {
        /** Max results */
        limit?: number;
    }) => apiFetch<{ id: string; source_variant_id: string; name?: string; slug?: string; description?: string; primary_image_url: string; thumbnail_url?: string; additional_images?: string[]; style_tags?: string[]; color_palette?: Record<string, unknown>; design_category_id?: string; owner_user_id?: string; is_public?: boolean; is_template?: boolean; is_featured?: boolean; view_count?: number; use_count?: number; like_count?: number; status: 'draft' | 'active' | 'archived' | 'deleted'; created_at: string; updated_at?: string }[]>(`/designs/featured`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get my designs
     * @param queryParams.include_archived - Include archived designs
     * @param queryParams.limit - Max results
     * @param queryParams.offset - Offset for pagination
     */
    getMyDesignsApiV1DesignsMyDesignsGet: (queryParams?: {
        /** Include archived designs */
        include_archived?: boolean;
        /** Max results */
        limit?: number;
        /** Offset for pagination */
        offset?: number;
    }) => apiFetch<ApiTypes.DesignAssetListResponse>(`/designs/my-designs`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Get design by slug
     */
    getDesignBySlugApiV1DesignsSlugSlugGet: (pathParams: {
        slug: string;
    }) => apiFetch<ApiTypes.DesignAssetResponse>(`/designs/slug/${pathParams.slug}`, 'GET'),

    /**
     * Get design asset
     */
    getDesignApiV1DesignsDesignIdGet: (pathParams: {
        design_id: string;
    }) => apiFetch<ApiTypes.DesignAssetResponse>(`/designs/${pathParams.design_id}`, 'GET'),

    /**
     * Update design asset
     */
    updateDesignApiV1DesignsDesignIdPatch: (pathParams: {
        design_id: string;
    }, body: ApiTypes.UpdateDesignAssetRequest) => apiFetch<ApiTypes.DesignAssetResponse>(`/designs/${pathParams.design_id}`, 'PATCH', { body }),

    /**
     * Delete design asset
     */
    deleteDesignApiV1DesignsDesignIdDelete: (pathParams: {
        design_id: string;
    }) => apiFetch<void>(`/designs/${pathParams.design_id}`, 'DELETE'),

    /**
     * Publish design
     */
    publishDesignApiV1DesignsDesignIdPublishPost: (pathParams: {
        design_id: string;
    }) => apiFetch<ApiTypes.DesignAssetResponse>(`/designs/${pathParams.design_id}/publish`, 'POST'),

    /**
     * Unpublish design
     */
    unpublishDesignApiV1DesignsDesignIdUnpublishPost: (pathParams: {
        design_id: string;
    }) => apiFetch<ApiTypes.DesignAssetResponse>(`/designs/${pathParams.design_id}/unpublish`, 'POST'),

    /**
     * Like design
     */
    likeDesignApiV1DesignsDesignIdLikePost: (pathParams: {
        design_id: string;
    }) => apiFetch<void>(`/designs/${pathParams.design_id}/like`, 'POST'),

    /**
     * Track design use
     */
    trackDesignUseApiV1DesignsDesignIdUsePost: (pathParams: {
        design_id: string;
    }) => apiFetch<void>(`/designs/${pathParams.design_id}/use`, 'POST'),

    /**
     * Get available product types
     * @param queryParams.country_code - Country code for pricing (ISO 2-letter)
     */
    getAvailableProductTypesApiV1DesignsDesignIdProductTypesGet: (pathParams: {
        design_id: string;
    }, queryParams?: {
        /** Country code for pricing (ISO 2-letter) */
        country_code?: string;
    }) => apiFetch<Record<string, unknown>[]>(`/designs/${pathParams.design_id}/product-types`, 'GET', queryParams ? { queryParams: Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v ?? "")])) } : {}),

    /**
     * Generate Mockup
     */
    generateMockupApiV1MockupsGeneratePost: (body: ApiTypes.GenerateMockupRequest) => apiFetch<ApiTypes.MockupResponse>(`/mockups/generate`, 'POST', { body }),

    /**
     * Generate Batch Mockups
     */
    generateBatchMockupsApiV1MockupsGenerateBatchPost: (body: ApiTypes.BatchMockupRequest) => apiFetch<ApiTypes.BatchMockupResponse>(`/mockups/generate/batch`, 'POST', { body }),

    /**
     * Check 3D Status
     */
    check3dStatusApiV1Mockups3dStatusPost: (body: ApiTypes.Check3DStatusRequest) => apiFetch<ApiTypes.Check3DStatusResponse>(`/mockups/3d/status`, 'POST', { body }),

    /**
     * List Available Templates
     */
    listAvailableTemplatesApiV1MockupsTemplatesGet: () => apiFetch<ApiTypes.TemplatesListResponse>(`/mockups/templates`, 'GET'),

    /**
     * Generate Multiview Enhancement
     */
    generateMultiviewEnhancementApiV1EnhancementMultiviewPost: (body: ApiTypes.Body_generate_multiview_enhancement_api_v1_enhancement_multiview_post) => apiFetch<ApiTypes.MultiviewEnhancementResponse>(`/enhancement/multiview`, 'POST', { body }),

    /**
     * Capture Email
     */
    captureEmail: (body: ApiTypes.CaptureEmailRequest) => apiFetch<ApiTypes.CaptureEmailResponse>(`/marketing/capture-email`, 'POST', { body }),

    /**
     * Trigger Abandoned Design Recovery
     */
    triggerAbandonedDesignRecoveryApiV1ScheduledTasksAbandonedDesignRecoveryPost: () => apiFetch<ApiTypes.TaskResponse>(`/scheduled-tasks/abandoned-design-recovery`, 'POST'),

    /**
     * Trigger Abandoned Cart Recovery
     */
    triggerAbandonedCartRecoveryApiV1ScheduledTasksAbandonedCartRecoveryPost: () => apiFetch<ApiTypes.TaskResponse>(`/scheduled-tasks/abandoned-cart-recovery`, 'POST'),

    /**
     * Trigger Refresh Exchange Rates
     */
    triggerRefreshExchangeRatesApiV1ScheduledTasksRefreshExchangeRatesPost: () => apiFetch<ApiTypes.TaskResponse>(`/scheduled-tasks/refresh-exchange-rates`, 'POST'),

    /**
     * Trigger Check Exchange Rate Validity
     */
    triggerCheckExchangeRateValidityApiV1ScheduledTasksCheckExchangeRateValidityPost: () => apiFetch<ApiTypes.TaskResponse>(`/scheduled-tasks/check-exchange-rate-validity`, 'POST'),

    /**
     * Trigger Gelato Catalog Sync
     */
    triggerGelatoCatalogSyncApiV1ScheduledTasksGelatoCatalogSyncPost: () => apiFetch<ApiTypes.TaskResponse>(`/scheduled-tasks/gelato-catalog-sync`, 'POST'),

    /**
     * Trigger Gelato Catalog Health Check
     */
    triggerGelatoCatalogHealthCheckApiV1ScheduledTasksGelatoCatalogHealthCheckPost: () => apiFetch<ApiTypes.TaskResponse>(`/scheduled-tasks/gelato-catalog-health-check`, 'POST'),

    /**
     * Root
     */
    root__get: () => apiFetch<Record<string, unknown>>(`/`, 'GET'),

    /**
     * Health
     */
    health_health_get: () => apiFetch<Record<string, unknown>>(`/health`, 'GET'),

    /**
     * Readiness
     */
    readiness_health_ready_get: () => apiFetch<Record<string, unknown>>(`/health/ready`, 'GET'),

    /**
     * Liveness
     */
    liveness_health_live_get: () => apiFetch<Record<string, unknown>>(`/health/live`, 'GET'),

    /**
     * Database Health
     */
    database_health_health_db_get: () => apiFetch<Record<string, unknown>>(`/health/db`, 'GET'),

    /**
     * Healthz
     */
    healthz_healthz_get: () => apiFetch<Record<string, unknown>>(`/healthz`, 'GET'),

    /**
     * Readyz
     */
    readyz_readyz_get: () => apiFetch<Record<string, unknown>>(`/readyz`, 'GET'),

    /**
     * Metrics
     */
    metrics_metrics_get: () => apiFetch<Record<string, unknown>>(`/metrics`, 'GET'),

};

export const apiClient = api;